import { Radio } from "@mui/material";
import type { ChangeEvent, Dispatch, ReactNode, SetStateAction } from "react";
import React, { useState } from "react";
import type {
  ExamForm,
  ExamFormTableColumnAnswer,
  ExamFormTableRow,
  ExamFormTestTable,
} from "@rmh/defs";
import AdditionalCommentsInput from "../inputs/AdditionalCommentsInput";
import TestFormContainer from "../inputs/TestFormContainer";

interface DisplayRLTableChoiceProps {
  item: ExamFormTestTable;
  examId: string;
  onInputChange?: () => void;
  setExamState?: Dispatch<SetStateAction<ExamForm[]>>;
  statusComponent: ReactNode;
  disabled: boolean;
}

function DisplayRlTableChoice({
  item,
  examId,
  onInputChange,
  setExamState,
  statusComponent,
  disabled,
}: DisplayRLTableChoiceProps){
  const getDoubleArrayValue = (input: ExamFormTableColumnAnswer[][], column: number, index: number) => {
    if (!Array.isArray(input) || !Array.isArray(input[column])) {
      return "";
    }
    return input[column][index]?.inputValueComment ?? "";
  }
  const defaultValues = item.examFormTableColumnAnswers as
    | ExamFormTableColumnAnswer[][]
    | undefined;
  const defaultLeftValue = defaultValues
    ? defaultValues[0].reduce<string>((acc, cell, index) => {
        if (cell.inputValueBoolean && acc === "")
          return String(
            (item.examFormTableRows as ExamFormTableRow[])[index].id
          );
        return acc;
      }, "")
    : "";
  const defaultRightValue = defaultValues
    ? defaultValues[1].reduce<string>((acc, cell, index) => {
        if (cell.inputValueBoolean && acc === "")
          return String(
            (item.examFormTableRows as ExamFormTableRow[])[index].id
          );
        return acc;
      }, "")
    : "";
  const coloredRow = "bg-rmh-blue-50";
  const clearRow = "bg-white";
  const [left, setLeft] = useState<string>(defaultLeftValue);
  const [right, setRight] = useState<string>(defaultRightValue);
  const onChange =
    (column: number, index: number) =>
    (event: ChangeEvent<HTMLInputElement>): void => {
      if (column === 0) {
        setLeft(event.target.value);
      } else {
        setRight(event.target.value);
      }
      if (setExamState) {
        setExamState((examsMaster) => {
          const examToUpdate = examsMaster.find(
            (e) => e.examFormId === examId
          ) as ExamForm;
          const examToUpdateIndex = examsMaster.findIndex(
            (e) => e.examFormId === examId
          );
          const testToUpdate = examToUpdate.examFormTests.find(
            (e) => e.testTitle === item.testTitle
          ) as ExamFormTestTable;
          const testToUpdateIndex = examToUpdate.examFormTests.findIndex(
            (e) => e.testTitle === item.testTitle
          );
          if (!Array.isArray(testToUpdate.examFormTableColumnAnswers)) {
            testToUpdate.examFormTableColumnAnswers = new Array(2);
          }
          let rowsArray = testToUpdate.examFormTableColumnAnswers[column];
          if (!Array.isArray(rowsArray)) {
            rowsArray = new Array(item.examFormTableRows?.length);
          }
          rowsArray[index] = { inputValueBoolean: true };
          examToUpdate.examFormTests[testToUpdateIndex] = testToUpdate;
          examsMaster[examToUpdateIndex] = examToUpdate;
          return [...examsMaster];
        });
      }
      if (onInputChange) {
        onInputChange();
      }
    };

  const handleAddInputChange =
    (column: number, index: number) =>
    (value: string): void => {
      if (setExamState) {
        setExamState((examsMaster) => {
          const examToUpdate = examsMaster.find(
            (e) => e.examFormId === examId
          ) as ExamForm;
          const examToUpdateIndex = examsMaster.findIndex(
            (e) => e.examFormId === examId
          );
          const testToUpdate = examToUpdate.examFormTests.find(
            (e) => e.testTitle === item.testTitle
          ) as ExamFormTestTable;
          const testToUpdateIndex = examToUpdate.examFormTests.findIndex(
            (e) => e.testTitle === item.testTitle
          );
          if (!Array.isArray(testToUpdate.examFormTableColumnAnswers)) {
            testToUpdate.examFormTableColumnAnswers = new Array(2);
          }
          let rowsArray = testToUpdate.examFormTableColumnAnswers[column];
          if (!Array.isArray(rowsArray)) {
            rowsArray = new Array(item.examFormTableRows?.length);
          }
          if (typeof rowsArray[index] === "object") {
            (rowsArray as ExamFormTableColumnAnswer[])[index].inputValueComment = value;
          } else {
            rowsArray[index] = { inputValueComment: value };
          }
          examToUpdate.examFormTests[testToUpdateIndex] = testToUpdate;
          examsMaster[examToUpdateIndex] = examToUpdate;
          return [...examsMaster];
        });
      }
      if (onInputChange) {
        onInputChange();
      }
    };

  const handleCommentChange = (value: string): void => {
    if (setExamState) {
      setExamState((examsMaster) => {
        const examToUpdate = examsMaster.find(
          (e) => e.examFormId === examId
        ) as ExamForm;
        const examToUpdateIndex = examsMaster.findIndex(
          (e) => e.examFormId === examId
        );
        const testToUpdate = examToUpdate.examFormTests.find(
          (e) => e.testTitle === item.testTitle
        ) as ExamFormTestTable;
        const testToUpdateIndex = examToUpdate.examFormTests.findIndex(
          (e) => e.testTitle === item.testTitle
        );
        testToUpdate.commentsText = value;
        examToUpdate.examFormTests[testToUpdateIndex] = testToUpdate;
        examsMaster[examToUpdateIndex] = examToUpdate;
        return [...examsMaster];
      });
    }
    if (onInputChange) {
      onInputChange();
    }
  };

  return (
    <TestFormContainer
      statusSlot={statusComponent}
      title={item.testTitle ?? ""}
    >
      <div className="grid w-full grid-cols-3 divide-x divide-y break-all border-b border-r">
        <div className="flex-1 px-4 py-2 text-center text-lg font-semibold text-black" />
        <div
          className={`border-rmh-gray-300 flex-1 border border-b-0 border-r-0 px-4 py-2 text-center text-lg font-semibold text-black ${coloredRow}`}
        >
          Left
        </div>
        <div
          className={`border-rmh-gray-300 flex-1 border border-b-0 px-4 py-2 text-center text-lg font-semibold text-black ${coloredRow}`}
        >
          Right
        </div>
        {item.examFormTableRows?.map((row, index) => (
          <React.Fragment key={`rltable_${row.id}`}>
            <div
              className={`flex  items-center p-4 font-semibold text-black ${
                index % 2 === 0 ? clearRow : coloredRow
              }`}
            >
              {row.rowTitle}
            </div>
            <div
              className={`flex w-full flex-col p-4 ${
                index % 2 === 0 ? clearRow : coloredRow
              }`}
            >
              <div className="flex flex-row justify-center">
                <Radio
                  checked={left === row.id}
                  disabled={disabled ? left !== row.id : false}
                  onChange={onChange(0, index)}
                  value={row.id}
                />
              </div>
              {Boolean(row.addInputText) && (
                <AdditionalCommentsInput
                  defaultValue={getDoubleArrayValue(item.examFormTableColumnAnswers as ExamFormTableColumnAnswer[][], 0, index)}
                  disabled={disabled}
                  onChange={handleAddInputChange(0, index)}
                  placeholder="Comments"
                />
              )}
            </div>
            <div
              className={`flex w-full flex-col p-4 ${
                index % 2 === 0 ? clearRow : coloredRow
              }`}
            >
              <div className="flex flex-row justify-center">
                <Radio
                  checked={right === row.id}
                  disabled={disabled ? right !== row.id : false}
                  onChange={onChange(1, index)}
                  value={row.id}
                />
              </div>
              {Boolean(row.addInputText) && (
                <AdditionalCommentsInput
                  defaultValue={getDoubleArrayValue(item.examFormTableColumnAnswers as ExamFormTableColumnAnswer[][], 1, index)}
                  disabled={disabled}
                  onChange={handleAddInputChange(1, index)}
                  placeholder="Comments"
                />
              )}
            </div>
          </React.Fragment>
        ))}
      </div>
      {Boolean(item.comments) && (
        <AdditionalCommentsInput
          defaultValue={item.commentsText ?? ""}
          disabled={disabled}
          onChange={handleCommentChange}
        />
      )}
    </TestFormContainer>
  );
}

export default DisplayRlTableChoice;
